import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { getProductPath } from "../../config/products";
import StatusBadge from "./StatusBadge";
import ProductGalleryPlaceholder from "./ProductGalleryPlaceholder";
import { getPrimaryProductImage } from "../../lib/productMedia";

/**
 * Variant card shown on a product range page.
 */
export default function VariantCard({ product, index = 0 }) {
  const to = getProductPath(product);
  const keySpec = Array.isArray(product.specifications)
    ? product.specifications.slice(0, 2)
    : [];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.06 }}
      className="steel-card clip-frame group flex flex-col h-full"
      data-testid={`variant-card-${product.sku}`}
    >
      <Link to={to} className="block border-b border-border overflow-hidden bg-secondary/30">
        {getPrimaryProductImage(product) ? (
          <img
            src={getPrimaryProductImage(product)}
            alt={product.name}
            className="w-full aspect-[4/3] object-cover object-center group-hover:scale-[1.02] transition-transform duration-700"
            loading="lazy"
          />
        ) : (
          <ProductGalleryPlaceholder compact label="Product Image" />
        )}
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className="font-mono-2 text-[10px] tracking-[0.2em] uppercase text-[color:var(--gold)]">
            {product.wattage || product.slug?.toUpperCase()}
          </div>
          <StatusBadge status={product.status} />
        </div>

        <Link to={to} className="mt-2 block">
          <h3 className="display display-section text-lg leading-tight group-hover:text-[color:var(--gold)] transition-colors">
            {product.name}
          </h3>
        </Link>
        <div className="mt-1 font-mono-2 text-[10px] tracking-[0.15em] text-muted-foreground">
          {product.sku}
        </div>

        <div className="mt-3 space-y-1 flex-1">
          {keySpec.map((s) => (
            <div key={s.label} className="text-xs text-steel-text">
              <span className="text-foreground">{s.label}:</span> {s.value}
            </div>
          ))}
        </div>

        <Link
          to={to}
          className="mt-5 inline-flex items-center gap-1.5 font-mono-2 text-[11px] tracking-[0.18em] uppercase"
          style={{ color: "var(--gold)" }}
          data-testid={`variant-view-${product.sku}`}
        >
          View Product <ArrowUpRight size={13} />
        </Link>
      </div>
    </motion.div>
  );
}
